import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { usePageVisibility } from "react-page-visibility";
import { createAvatar } from "@dicebear/core";
import { miniavs } from "@dicebear/collection";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import AuthContext from "../context/AuthProvider";
import useLogout from "./useLogout";
import useAxiosPrivate from "./useAxiosPrivate";
import useRefreshToken from "./useRefreshToken";
import { useInterval } from "../utils/useInterval";
import {
  getPushState,
  isIOS,
  requestPushPermission,
  syncPushSubscription,
} from "../utils/pushNotifications";
import { clearLocalStoragePreservingPrefs } from "../layout/preservedStorage";

function removeNumberInParentheses(string) {
  const regex = /\(\d+\)/g;
  return string.replace(regex, "");
}

const PAGE_TITLES_AUTHED = {
  "/": "MySolon.gr",
  "/astika": "Αστικά | MySolon.gr",
  "/out": "Αστικά(Εκτός Σόλων) | MySolon.gr",
  "/adjustice": "Διοικητικά | MySolon.gr",
  "/imerologio": "Ημερολόγιο | MySolon.gr",
  "/eauction": "Eauction | MySolon.gr",
  "/case-stats": "Στατιστικά Υποθέσεων | MySolon.gr",
  "/poinika": "Πινάκια Ποινικών | MySolon.gr",
  "/etairies": "Εταιρίες | MySolon.gr",
  "/updates": "Επισκόπηση | MySolon.gr",
  "/calendar": "Ημερολόγιο | MySolon.gr",
};

const PAGE_TITLES_PUBLIC = {
  "/prothesmies/neataktiki": "Υπολογισμός Προθεσμιών Νέας Τακτικής | MySolon.gr",
  "/prothesmies/mikrodiafores": "Υπολογισμός Προθεσμιών Μικροδιαφορών | MySolon.gr",
  "/prothesmies/ste": "Υπολογισμός Προθεσμιών Νέας Δικονομίας ΣτΕ | MySolon.gr",
  "/prothesmies/diamesolavisi": "Υπολογισμός Προθεσμιών Διαμεσολάβησης | MySolon.gr",
  "/prothesmies/diatpliromis": "Υπολογισμός Προθεσμιών Διαταγών Πληρωμής | MySolon.gr",
  "/prothesmies/apopoiisis": "Υπολογισμός Προθεσμιών Αποποίησης Κληρονομίας | MySolon.gr",
};

const isNotificationUpdate = (r) =>
  r.court !== undefined ||
  r.like_id !== undefined ||
  r.comment_id !== undefined ||
  r.periohi !== undefined ||
  r.afm_eauction_id !== undefined ||
  r.appeal_update !== undefined ||
  r.pretrial_update !== undefined ||
  r.epan_update !== undefined;

// App-wide side effects that used to live inside Header: summary/updates
// polling, push registration, page titles, silent token refresh, app badge,
// notifications state and sign-out. Mount exactly one consumer at a time.
export default function useHeaderData() {
  const {
    auth,
    credits,
    setCredits,
    checking,
    setChecking,
    avatarUploaded,
    notifLength,
    setNotifLength,
    persist,
  } = useContext(AuthContext);
  const isVisible = usePageVisibility();

  const axiosPrivate = useAxiosPrivate();
  const refresh = useRefreshToken();
  const location = useLocation();
  const navigate = useNavigate();
  const logout = useLogout();
  const [showNotif, setShowNotif] = useState(false);
  const [fetchingUpdates, setFetchingUpdates] = useState(false);

  const [updates, setUpdates] = useState([]);
  const latestRequestRef = useRef(null);
  const latestAbortRef = useRef(null);
  const summaryRequestRef = useRef(null);
  const summaryAbortRef = useRef(null);
  const latestSummaryRevisionRef = useRef(null);
  const subscriptionSyncInFlightRef = useRef(false);

  const closeNotif = useCallback(() => {
    setShowNotif(false);
  }, []);

  // On app resume (visibility/focus) attempt a silent refresh if persist=true and no access token
  useEffect(() => {
    const handleVisible = () => {
      if (persist && !auth?.accessToken) {
        refresh().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisible);
    window.addEventListener("focus", handleVisible);
    return () => {
      document.removeEventListener("visibilitychange", handleVisible);
      window.removeEventListener("focus", handleVisible);
    };
  }, [persist, auth?.accessToken, refresh]);

  useEffect(() => {
    if (!navigator.setAppBadge) return;
    // Rejects on iOS until notifications are allowed; nothing to do then.
    try {
      Promise.resolve(navigator.setAppBadge(notifLength)).catch(() => {});
    } catch {}
  }, [notifLength]);

  const getSeen = async () => {
    try {
      await axiosPrivate.get("/updates/latest/seen");
      setNotifLength(0);
    } catch (err) {
      console.error(err);
    }
  };

  const signOut = async () => {
    clearLocalStoragePreservingPrefs();
    await logout();
    navigate("/");
  };

  const avatar = useMemo(() => {
    return createAvatar(miniavs, {
      size: 16,
      seed: auth?.uuid,
      backgroundType: ["gradientLinear", "solid"],
      backgroundColor: ["b6e3f4", "c0aede", "d1d4f9"],
    }).toDataUriSync();
  }, [auth?.uuid]);

  useEffect(() => {
    const titles = auth?.user ? PAGE_TITLES_AUTHED : PAGE_TITLES_PUBLIC;
    const title = titles[location.pathname];
    if (title) {
      document.title = title;
    }
    if (notifLength !== 0 && document.title.indexOf(")") === -1) {
      document.title = `(${notifLength}) ${document.title}`;
    }
    if (notifLength === 0 && document.title.indexOf(")") !== -1) {
      // Remove () from title
      document.title = removeNumberInParentheses(document.title);
    }
  }, [location, updates, notifLength, showNotif, auth?.user]);

  useEffect(() => {
    if (!auth?.user) {
      return;
    }

    const registerForPushNotifications = async () => {
      if (subscriptionSyncInFlightRef.current) {
        return;
      }
      subscriptionSyncInFlightRef.current = true;
      try {
        let state = getPushState();
        // Other browsers may still prompt without a tap. iOS never does, so
        // there PushPrompt asks from a button instead.
        if (state === "default" && !isIOS()) {
          state = await requestPushPermission();
        }
        if (state === "granted") {
          await syncPushSubscription(axiosPrivate, auth.user);
        }
      } catch (err) {
        console.error("Failed to register push subscription", err);
      } finally {
        subscriptionSyncInFlightRef.current = false;
      }
    };

    registerForPushNotifications();
  }, [auth?.user, axiosPrivate]);

  const applyLatestSummary = useCallback(
    (data) => {
      setCredits(data?.credits ?? 0);
      setChecking(Array.isArray(data?.checking) ? data.checking : []);
      const unseen = Number(data?.unseen_count ?? 0);
      localStorage.setItem("notif", unseen);
      setNotifLength(unseen);
      if (data?.revision) {
        const revisionChanged =
          latestSummaryRevisionRef.current !== null &&
          latestSummaryRevisionRef.current !== data.revision;
        latestSummaryRevisionRef.current = data.revision;
        return { revisionChanged, revision: data.revision };
      }
      return { revisionChanged: false, revision: null };
    },
    [setChecking, setCredits, setNotifLength]
  );

  const applyLatestUpdates = useCallback(
    (data) => {
      setUpdates(Array.isArray(data?.updates) ? data.updates : []);
      setCredits(data?.credits ?? 0);
      setChecking(Array.isArray(data?.checking) ? data.checking : []);
      const unseen =
        data?.unseen_count ??
        data?.updates?.filter((r) => r.seen !== 1)?.length ??
        0;
      localStorage.setItem("notif", unseen);
      setNotifLength(unseen);
      if (data?.revision) {
        latestSummaryRevisionRef.current = data.revision;
      }
    },
    [setChecking, setCredits, setNotifLength]
  );

  const fetchLatestSummary = useCallback(async () => {
    if (!auth?.user) return null;

    if (summaryRequestRef.current) {
      return summaryRequestRef.current;
    }

    if (summaryAbortRef.current) {
      summaryAbortRef.current.abort();
    }

    const controller = new AbortController();
    summaryAbortRef.current = controller;

    const request = (async () => {
      try {
        const response = await axiosPrivate.get("/updates/latest/summary", {
          signal: controller.signal,
          headers: {
            "Cache-Control": "no-cache",
          },
        });
        return applyLatestSummary(response.data);
      } catch (err) {
        console.error(err);
        if (!axios.isCancel(err)) {
          navigate("/login", { state: { from: location }, replace: true });
        }
        return null;
      } finally {
        if (summaryAbortRef.current === controller) {
          summaryAbortRef.current = null;
        }
        summaryRequestRef.current = null;
      }
    })();

    summaryRequestRef.current = request;
    return request;
  }, [applyLatestSummary, auth?.user, axiosPrivate, location, navigate]);

  const fetchLatestUpdates = useCallback(async () => {
    if (!auth?.user) return;

    if (latestRequestRef.current) {
      return latestRequestRef.current;
    }

    if (latestAbortRef.current) {
      latestAbortRef.current.abort();
    }

    const controller = new AbortController();
    latestAbortRef.current = controller;
    setFetchingUpdates(true);

    const request = (async () => {
      try {
        const response = await axiosPrivate.get("/updates/latest", {
          signal: controller.signal,
          headers: {
            "Cache-Control": "no-cache",
          },
        });
        applyLatestUpdates(response.data);
      } catch (err) {
        console.error(err);
        if (!axios.isCancel(err)) {
          navigate("/login", { state: { from: location }, replace: true });
        }
      } finally {
        setFetchingUpdates(false);
        if (latestAbortRef.current === controller) {
          latestAbortRef.current = null;
        }
        latestRequestRef.current = null;
      }
    })();

    latestRequestRef.current = request;
    return request;
  }, [applyLatestUpdates, auth?.user, axiosPrivate, location, navigate]);

  const handleShowNotif = async () => {
    const willOpen = !showNotif;
    setShowNotif(willOpen);
    if (!willOpen) return;
    const summaryResult = await fetchLatestSummary();
    if (summaryResult?.revisionChanged || updates.length === 0) {
      await fetchLatestUpdates();
    }
  };

  useEffect(() => {
    if (!auth?.user) return;
    void fetchLatestSummary();
  }, [auth?.user, fetchLatestSummary]);

  useEffect(() => {
    if (!auth?.user) return;
    const onFocus = async () => {
      const summaryResult = await fetchLatestSummary();
      if (showNotif && (summaryResult?.revisionChanged || updates.length === 0)) {
        await fetchLatestUpdates();
      }
    };
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  }, [auth?.user, fetchLatestSummary, fetchLatestUpdates, showNotif, updates.length]);

  useEffect(() => {
    return () => {
      latestAbortRef.current?.abort();
      summaryAbortRef.current?.abort();
    };
  }, []);

  useInterval(
    async () => {
      if (
        auth?.user &&
        !fetchingUpdates &&
        !latestRequestRef.current &&
        !summaryRequestRef.current
      ) {
        const summaryResult = await fetchLatestSummary();
        if (showNotif && (summaryResult?.revisionChanged || updates.length === 0)) {
          await fetchLatestUpdates();
        }
      }
    },
    isVisible ? 8000 : 120000
  );

  const notificationUpdates = useMemo(
    () => updates.filter(isNotificationUpdate),
    [updates]
  );

  return {
    auth,
    credits,
    checking,
    avatarUploaded,
    avatar,
    notifLength,
    showNotif,
    closeNotif,
    handleShowNotif,
    getSeen,
    signOut,
    notificationUpdates,
    location,
  };
}
